/**
 * 提示音
 *
 * 用 Web Audio 而不是 <audio>：HTMLAudioElement 调 play() 时若数据尚未就位，
 * 起播时刻由浏览器自行决定，标签页切到后台被节流时经常吞掉开头
 * （「你有新的消息」只听到「有新的消息」）。这里预先把整段音频解码成 AudioBuffer 常驻内存，
 * 每次播放新建 source 从 0 播起，起播点确定，不受网络与解码抖动影响。
 *
 * 节流同样是必须的：会话密集时消息一条接一条，不限频会连成一片噪音，反而让人忽略提醒。
 */

/** 音频文件放在 public 下，构建后可直接按根路径取 */
const SOUND_URL = '/sound.mp3';

/** 节流窗口：这段时间内无论来多少次触发，只响一次 */
const THROTTLE_MS = 5000;
/** 同一条消息可能同时被 IM 推送和未读轮询捕获，短时间内只播放一次 */
const MESSAGE_DEDUPE_MS = 60_000;

let ctx: AudioContext | null = null;
let buffer: AudioBuffer | null = null;
/** 解码只做一次，结果复用；失败置空，播放时回落到 <audio> */
let decoding: Promise<void> | null = null;

/** 浏览器不支持 Web Audio 时构造会抛错，此时返回 null 走兜底 */
const getCtx = (): AudioContext | null => {
  if (ctx) return ctx;
  try {
    ctx = new AudioContext();
  } catch {
    ctx = null;
  }
  return ctx;
};

const ensureBuffer = (): Promise<void> => {
  if (decoding) return decoding;
  decoding = (async () => {
    const c = getCtx();
    if (!c) return;
    const res = await fetch(SOUND_URL);
    buffer = await c.decodeAudioData(await res.arrayBuffer());
  })().catch(() => {
    buffer = null;
  });
  return decoding;
};

/** Web Audio 播放；任一环节不可用返回 false，交给兜底 */
const playViaWebAudio = async (): Promise<boolean> => {
  const c = getCtx();
  if (!c) return false;
  await ensureBuffer();
  if (!buffer) return false;
  /** 用户与页面交互前 AudioContext 停在 suspended，resume 失败说明浏览器还不允许出声 */
  if (c.state === 'suspended') {
    try {
      await c.resume();
    } catch {
      return false;
    }
  }
  const source = c.createBufferSource();
  source.buffer = buffer;
  source.connect(c.destination);
  source.start();
  return true;
};

/** 兜底：退回 <audio>，开头仍可能被截，但有声比没声强 */
const playViaAudioElement = (): void => {
  try {
    void new Audio(SOUND_URL).play().catch(() => {});
  } catch {
    /* 无音频设备等情况忽略 */
  }
};

const emit = (): void => {
  playViaWebAudio()
    .then((ok) => {
      if (!ok) playViaAudioElement();
    })
    .catch(() => playViaAudioElement());
};

/**
 * 预热：应用挂载后调用一次。
 * 提前把音频解码好，并在首次交互时唤醒 AudioContext——浏览器在用户与页面交互前禁止出声，
 * 不先 resume 的话第一声提醒会被直接拦掉。
 */
export function primeNotificationSound(): void {
  void ensureBuffer();
  const wake = () => {
    void getCtx()?.resume();
    void ensureBuffer();
  };
  (['pointerdown', 'keydown'] as const).forEach((type) =>
    window.addEventListener(type, wake, { once: true, passive: true }),
  );
}

let lastMessageAt = 0;
const playedMessageIds = new Map<string, number>();

/** 新消息提示音 */
export function playNewMessageSound(messageId?: number | string): void {
  const now = Date.now();
  if (messageId != null) {
    const key = String(messageId);
    const playedAt = playedMessageIds.get(key);
    if (playedAt != null && now - playedAt < MESSAGE_DEDUPE_MS) return;
    playedMessageIds.set(key, now);
    /** 限制缓存体积，并顺手清理已经失效的记录 */
    if (playedMessageIds.size > 200) {
      playedMessageIds.forEach((time, id) => {
        if (now - time >= MESSAGE_DEDUPE_MS) playedMessageIds.delete(id);
      });
      while (playedMessageIds.size > 200) {
        const oldestId = playedMessageIds.keys().next().value;
        if (oldestId == null) break;
        playedMessageIds.delete(oldestId);
      }
    }
  }
  if (now - lastMessageAt < THROTTLE_MS) return;
  lastMessageAt = now;
  emit();
}

let lastAssignAt = 0;

/** 访客进线 / 客资分配提示音；与新消息各自计时，两类提醒不互相吞 */
export function playAssignNotifySound(): void {
  const now = Date.now();
  if (now - lastAssignAt < THROTTLE_MS) return;
  lastAssignAt = now;
  emit();
}
